import type { TaskLikeObject } from '@frontend/api-client';
import {
  isTaskAwaitingMemberResponse,
  isTaskMarkedAsFailedToComplete,
  isTaskMarkedAsSuccessfullyCompleted,
} from '@frontend/api-client';
import { useFeatureIsOn, useFeatureValue, useMemo } from '@frontend/react';
import { AwaitingMemberResponseTaskBadge, CompletedTaskBadge, FailedToCompleteTaskBadge } from '@frontend/web-icons';
import { Icon, YStack } from '@frontend/web-react';
import { getIconNameByTaskCategory } from './helper';

type TaskCategoryIconProps = {
  task: TaskLikeObject;
};

export const TaskCategoryIcon = ({ task }: TaskCategoryIconProps) => {
  const { value: markAsDoneDeployDate } = useFeatureValue('mark_as_done_deploy_date');
  const { enabled: isGameplanPricingEnabled } = useFeatureIsOn('is_gameplan_pricing_enabled');
  const { clusterLabel } = task;

  const badgeIcon = useMemo(() => {
    // Is draft task
    if (!('memberConfirmationStatus' in task)) return null;
    if (isTaskAwaitingMemberResponse(task)) return <AwaitingMemberResponseTaskBadge />;
    if (isTaskMarkedAsSuccessfullyCompleted(task, markAsDoneDeployDate)) return <CompletedTaskBadge />;
    if (isTaskMarkedAsFailedToComplete(task)) return <FailedToCompleteTaskBadge />;
    return null;
  }, [task, markAsDoneDeployDate]);

  if (isGameplanPricingEnabled) {
    return (
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      <YStack accessibilityLabel={`${clusterLabel} task category icon`} bg="neutral100" centered className="relative size-[42px]" radius="xxxl">
        <YStack>
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
          <YStack alignItems="center" className="items-center justify-center size-[42px]" justifyContent="center">
            <Icon color="neutral" name={getIconNameByTaskCategory(clusterLabel)} size={20} />
          </YStack>
        </YStack>
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value,tailwindcss/no-custom-classname */}
        <YStack className="bottom-none right-none size-[13px] absolute">{badgeIcon}</YStack>
      </YStack>
    );
  }

  return (
    // eslint-disable-next-line tailwindcss/no-arbitrary-value
    <YStack bg="neutral100" centered className="relative size-[48px]" radius="xxxl">
      <YStack>
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <YStack alignItems="center" className="items-center justify-center size-[48]" justifyContent="center">
          <Icon color="neutral" name={getIconNameByTaskCategory(clusterLabel)} size={29} />
        </YStack>
      </YStack>
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value,tailwindcss/no-custom-classname */}
      <YStack className="bottom-none right-none size-[16px] absolute">{badgeIcon}</YStack>
    </YStack>
  );
};
